import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { z } from "zod";

import { usePostOAuth2CallbackParams } from "@/clients/deflow";
import { TokenKeys } from "@/util/TokenKeys";

const callbackSearchSchema = z.object({
  code: z.string().optional(),
  state: z.string().optional(),
  error: z.string().optional(),
});

export const Route = createFileRoute("/oauth2/callback")({
  validateSearch: (search) => callbackSearchSchema.parse(search),
  component: OAuth2Callback,
});

function OAuth2Callback() {
  const { code, state } = Route.useSearch();
  const [cookies, , removeCookie] = useCookies([TokenKeys.AccessToken]);

  const { trigger, isMutating } = usePostOAuth2CallbackParams(
    cookies[TokenKeys.AccessToken],
  );

  const [linkSuccess, setLinkSuccess] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    const callback = async () => {
      if (typeof code === "string" && typeof state === "string") {
        if (typeof cookies[TokenKeys.AccessToken] !== "string") {
          setLinkSuccess(false);
          return;
        }

        const [link, error] = await trigger({ code, state });

        if (link?.success) {
          setLinkSuccess(true);
        } else if (error) {
          setLinkSuccess(false);
        }
        removeCookie(TokenKeys.AccessToken);
      }
    };
    callback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, state]);

  return (
    <main className="flex items-center justify-center h-screen px-8">
      {isMutating && <p>Linking...</p>}
      {linkSuccess && (
        <p>
          Accounts successfully linked. You can close this page and go back to
          the Figma plugin.
        </p>
      )}
      {linkSuccess === false && (
        <p>
          Could not link accounts. Please go back to the Figma plugin and click{" "}
          <strong>Retry</strong>.
        </p>
      )}
    </main>
  );
}
